import { useNavigate, useParams } from "react-router-dom";
import { BackButton } from "@/components/BackButton";
import { useEffect, useMemo, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Panel } from "primereact/panel";
import {
  BasicInfoFields,
  BookingInfoFields,
  ConsentFields,
  TagsField,
} from "@/components/events/EventFormFields";
import { FormActions, ImageUploadField } from "@/components/forms";
import { buildUpdateEventPayloadFromEvent } from "@/lib/event-utils";
import type { EventFormData, BookableEventFormData, NonBookableEventFormData } from "@/lib/event-schemas";
import { handleApiError } from "@/lib/error-handling";
import { useAdminEvent, useAdminEventUpdate } from "@/lib/admin-api";
import { useAdminTagsList } from "@/lib/admin-api";
import { confirmAsync, useToast } from "@/lib/ui";
import { useEventEditForm } from "./hooks/useEventEditForm";
import { useWatch } from "react-hook-form";
import type { UseFormRegister, UseFormSetValue, UseFormWatch, Control } from "react-hook-form";

export function EventEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toastRef, showToast } = useToast();
  const [saving, setSaving] = useState(false);
  const eventQuery = useAdminEvent(id);
  const event = eventQuery.data?.event ?? null;

  const tagsQuery = useAdminTagsList({
    page: 1,
    pageSize: 100,
    sortField: "name",
    sortOrder: 1,
  });

  const availableTags = useMemo(() => {
    const options = (tagsQuery.data?.data ?? []).map((tag) => ({
      label: tag.name,
      value: tag.name,
    }));

    const selectedFromEvent =
      event?.tags?.map((t: { tag: { name: string } }) => ({ label: t.tag.name, value: t.tag.name })) ?? [];

    const merged = [...options];
    const byValue = new Set(merged.map((t) => t.value));
    for (const sel of selectedFromEvent) {
      if (!byValue.has(sel.value)) {
        merged.push(sel);
        byValue.add(sel.value);
      }
    }
    return merged;
  }, [event?.tags, tagsQuery.data?.data]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
    resetWithEvent,
    setError,
  } = useEventEditForm();

  const updateMutation = useAdminEventUpdate();

  useEffect(() => {
    if (!id) {
      void navigate("/events");
    }
  }, [id, navigate]);

  const hasNavigatedAwayRef = useRef(false);
  useEffect(() => {
    if (hasNavigatedAwayRef.current) return;
    if (!id) return;

    if (eventQuery.isError) {
      hasNavigatedAwayRef.current = true;
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Failed to load event",
      });
      void navigate("/events");
      return;
    }

    if (eventQuery.isSuccess && !eventQuery.isFetching && !eventQuery.data?.event) {
      hasNavigatedAwayRef.current = true;
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Event not found",
      });
      void navigate("/events");
      return;
    }

    const loadedEvent = eventQuery.data?.event;
    if (!loadedEvent) return;

    resetWithEvent(loadedEvent);
  }, [
    eventQuery.data,
    eventQuery.isError,
    eventQuery.isFetching,
    eventQuery.isSuccess,
    id,
    navigate,
    resetWithEvent,
    showToast,
  ]);

  const onSubmit = async (formData: EventFormData) => {
    if (!event || !id) return;

    const publishNow = await confirmAsync({
      message: "Do you want to update the publishing status or keep it as is?",
      header: "Save Changes",
      icon: "pi pi-question-circle",
      acceptLabel: "Publish Now",
      rejectLabel: "Keep as Draft/Existing",
      defaultFocus: "reject",
    });
    setSaving(true);
    try {
      const nextIsPublished = publishNow ? true : (event.isPublished ?? false);

      if (formData.isBookable) {
        const bookableData = formData as BookableEventFormData;
        const updatePayload = buildUpdateEventPayloadFromEvent(event, {
          eventKind: bookableData.eventKind,
          title: bookableData.title,
          description: bookableData.description,
          timezone: bookableData.timezone,
          location: bookableData.location ?? null,
          schedule: bookableData.schedule ?? null,
          whatToBring: bookableData.whatToBring ?? null,
          audience: bookableData.audience,
          requiresConsentForm: bookableData.requiresConsentForm,
          requiresPhotoConsent: bookableData.requiresPhotoConsent,
          groupsEnabled: bookableData.groupsEnabled,
          bookingClosesAt: bookableData.bookingClosesAt?.toISOString() || null,
          imageUrl: bookableData.imageUrl ?? null,
          tags: bookableData.tags,
          isPublished: nextIsPublished,
          isFeatured: bookableData.isFeatured,
        });

        try {
          await updateMutation.mutateAsync({
            params: {
              path: { eventId: id },
            },
            body: updatePayload,
          });
        } catch (apiError: unknown) {
          const { message: errorMessage, hasValidationErrors } = handleApiError<
            BookableEventFormData
          >(apiError, setError);

          showToast({
            severity: "error",
            summary: hasValidationErrors ? "Validation Error" : "Error",
            detail: errorMessage,
          });

          if (!hasValidationErrors) {
            throw new Error(errorMessage);
          }
          return;
        }
      } else {
        const nonBookableData = formData as NonBookableEventFormData;
        const updatePayload = buildUpdateEventPayloadFromEvent(event, {
          eventKind: nonBookableData.eventKind,
          title: nonBookableData.title,
          description: nonBookableData.description,
          timezone: nonBookableData.timezone,
          location: nonBookableData.location ?? null,
          startsAt: nonBookableData.startsAt.toISOString(),
          endsAt: nonBookableData.endsAt.toISOString(),
          tags: nonBookableData.tags,
          isRecurring: nonBookableData.isRecurring ?? false,
          rrule: nonBookableData.rrule ?? null,
          isPublished: nextIsPublished,
        });

        try {
          await updateMutation.mutateAsync({
            params: {
              path: { eventId: id },
            },
            body: updatePayload,
          });
        } catch (apiError: unknown) {
          const { message: errorMessage, hasValidationErrors } = handleApiError<
            NonBookableEventFormData
          >(apiError, setError);

          showToast({
            severity: "error",
            summary: hasValidationErrors ? "Validation Error" : "Error",
            detail: errorMessage,
          });

          if (!hasValidationErrors) {
            throw new Error(errorMessage);
          }
          return;
        }
      }

      showToast({
        severity: "success",
        summary: "Success",
        detail: "Event updated successfully",
      });

      void navigate(`/events/${id}`);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update event";
      showToast({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
      });
    } finally {
      setSaving(false);
    }
  };

  if (eventQuery.isLoading) {
    return (
      <div className="card">
        <div className="text-center p-4">Loading...</div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  const isBookable = Boolean(useWatch({ control, name: "isBookable" }));
  const bookableRegister = register as UseFormRegister<BookableEventFormData>;
  const nonBookableRegister = register as UseFormRegister<NonBookableEventFormData>;
  const bookableWatch = watch as UseFormWatch<BookableEventFormData>;
  const nonBookableWatch = watch as UseFormWatch<NonBookableEventFormData>;
  const bookableSetValue = setValue as UseFormSetValue<BookableEventFormData>;
  const nonBookableSetValue = setValue as UseFormSetValue<NonBookableEventFormData>;
  const bookableControl = control as unknown as Control<BookableEventFormData>;
  const nonBookableControl = control as unknown as Control<NonBookableEventFormData>;

  return (
    <div className="card">
      <Toast ref={toastRef} />
      <ConfirmDialog />
      <div className="mb-4">
        <div className="flex align-items-center gap-3 mb-4">
          <BackButton />
          <h1 className="text-2xl font-bold m-0">Edit Event Details</h1>
        </div>
        {isBookable && (
          <p className="text-gray-500 mt-1">
            Note: To manage sessions or price points, use the event view page.
          </p>
        )}
      </div>

      <form
        onSubmit={(e) => {
          // TypeScript can't properly narrow discriminated unions in handleSubmit types
          void (handleSubmit(onSubmit) as unknown as (e: React.FormEvent<HTMLFormElement>) => Promise<void>)(e);
        }}
        className="flex flex-column gap-4"
      >
        {isBookable ? (
          <BasicInfoFields
            register={bookableRegister}
            errors={errors as typeof errors & { _type: BookableEventFormData }}
            control={bookableControl}
            isBookable={true}
          />
        ) : (
          <BasicInfoFields
            register={nonBookableRegister}
            errors={errors as typeof errors & { _type: NonBookableEventFormData }}
            control={nonBookableControl}
            watch={nonBookableWatch}
            setValue={nonBookableSetValue}
            isBookable={false}
          />
        )}

        {isBookable && (
          <>
            <Panel header="Event Images">
              <ImageUploadField
                id="imageUrl"
                label="Event Image"
                value={bookableWatch("imageUrl") || null}
                onChange={(url) => bookableSetValue("imageUrl", url)}
                error={(errors as typeof errors & { imageUrl?: { message?: string } }).imageUrl?.message}
              />
            </Panel>

            <BookingInfoFields
              control={bookableControl}
              errors={errors as typeof errors & { _type: BookableEventFormData }}
              referenceDate={event?.startsAt ? new Date(event.startsAt) : null}
              referenceDateLabel="event start"
            />

            <ConsentFields
              control={bookableControl}
            />
          </>
        )}

        <TagsField
          control={control}
          errors={errors}
          availableTags={availableTags}
        />

        <FormActions
          primaryLabel="Save Changes"
          onPrimary={() => {
            // TypeScript can't properly narrow discriminated unions in handleSubmit types
            void (handleSubmit(onSubmit) as unknown as () => Promise<void>)();
          }}
          primarySubmit
          primaryLoading={saving}
          secondaryLabel="Cancel"
          onSecondary={() => {
            void navigate(`/events/${id}`);
          }}
        />
      </form>
    </div>
  );
}
